import {
  MEDIA_TYPES,
  mediaUploadReceipt,
  type MediaHoldCode,
  type MediaUploadReceipt,
  type MediaVariant,
} from '@servwell/shared';

/**
 * Getting a phone photo small enough to send from a driveway (PRD v2 §5).
 *
 * A modern phone shoots 8–12MB. On one bar of LTE outside a house that is a minute of
 * spinner and a decent chance of failure, and a tech who has watched that happen twice
 * stops taking photos. Downscaling in the browser turns it into ~200–400KB, which sends
 * in a second and is still more detail than anyone will ever zoom into.
 *
 * 1600px is the long edge because it survives being viewed full-screen on a desk monitor
 * and is still far below what a rusted heat exchanger needs to be legible.
 */
const MAX_EDGE = 1600;
const QUALITY = 0.82;

export interface PreparedImage {
  blob: Blob;
  contentType: string;
  filename: string;
  width: number;
  height: number;
  /** When the picture was TAKEN, if the file says so — not when it was uploaded. */
  takenAt: Date | null;
}

/** The file's own timestamp is the closest thing to "when" the browser will give us. */
function takenAtOf(file: File): Date | null {
  if (!file.lastModified) return null;
  const d = new Date(file.lastModified);
  return Number.isNaN(d.getTime()) ? null : d;
}

function loadBitmap(file: File): Promise<ImageBitmap | HTMLImageElement> {
  // createImageBitmap applies EXIF orientation for us; without it, portrait photos from
  // some phones arrive sideways and every one of them looks like a bug.
  if ('createImageBitmap' in window) {
    return createImageBitmap(file, {
      imageOrientation: 'from-image',
    } as ImageBitmapOptions);
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('That file could not be read as an image'));
    };
    img.src = url;
  });
}

/**
 * Shrink to fit, re-encode as JPEG. Returns the ORIGINAL untouched when it's already small
 * or when anything about the canvas path fails — a photo that arrives at full size is a
 * slow upload, but a photo that doesn't arrive is a lost record.
 */
export async function prepareImage(file: File): Promise<PreparedImage> {
  const takenAt = takenAtOf(file);
  const fallback: PreparedImage = {
    blob: file,
    contentType: file.type || 'image/jpeg',
    filename: file.name || 'photo.jpg',
    width: 0,
    height: 0,
    takenAt,
  };

  try {
    const source = await loadBitmap(file);
    const w = 'width' in source ? source.width : 0;
    const h = 'height' in source ? source.height : 0;
    if (!w || !h) return fallback;

    const scale = Math.min(1, MAX_EDGE / Math.max(w, h));
    const outW = Math.round(w * scale);
    const outH = Math.round(h * scale);

    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d');
    if (!ctx) return { ...fallback, width: w, height: h };
    ctx.drawImage(source as CanvasImageSource, 0, 0, outW, outH);
    if ('close' in source) source.close();

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', QUALITY),
    );
    if (!blob) return { ...fallback, width: w, height: h };

    // A re-encode that came out bigger (a small PNG screenshot, say) isn't worth taking.
    if (blob.size >= file.size && scale === 1) {
      return { ...fallback, width: w, height: h };
    }

    return {
      blob,
      contentType: 'image/jpeg',
      filename: (file.name || 'photo').replace(/\.[^.]+$/, '') + '.jpg',
      width: outW,
      height: outH,
      takenAt,
    };
  } catch {
    return fallback;
  }
}

/** Where a stored attachment's bytes are served from — always through the API. */
export const attachmentUrl = (id: string, variant?: MediaVariant) =>
  `/api/attachments/${id}/file${variant ? `?variant=${variant}` : ''}`;

/** Preserve the selected original. Preview decoding is deliberately not a prerequisite. */
export async function prepareOriginalImage(
  file: File,
  maxBytes: number,
): Promise<PreparedImage & { sha256: string }> {
  if (file.size > maxBytes)
    throw new Error(
      `That photo is larger than ${maxBytes / 1_000_000} MB. It wasn’t kept on this phone.`,
    );
  if (file.size === 0)
    throw new Error('That photo is empty. It wasn’t kept on this phone.');
  const contentType =
    file.type ||
    (/\.hei[cf]$/i.test(file.name)
      ? /\.heif$/i.test(file.name)
        ? 'image/heif'
        : 'image/heic'
      : '');
  if (!(MEDIA_TYPES as readonly string[]).includes(contentType)) {
    throw new Error(
      'Choose a JPEG, PNG, WebP, HEIC or HEIF photo. Videos and other files aren’t supported here.',
    );
  }
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return {
    blob: file,
    filename: file.name || 'photo.jpg',
    contentType,
    width: 0,
    height: 0,
    takenAt: takenAtOf(file),
    sha256: [...new Uint8Array(digest)]
      .map((b) => b.toString(16).padStart(2, '0'))
      .join(''),
  };
}

/**
 * One photo, ready to send. The idempotency envelope is not optional here: this payload is
 * written to disk and retried until the server answers, so `mutationId` is what stops a
 * reply lost in flight from becoming a second copy of the photograph.
 */
export interface UploadPayload {
  clientId: string;
  mutationId: string;
  entityType: string;
  entityId: string;
  filename: string;
  contentType: string;
  width: number;
  height: number;
  /** ISO, so the payload survives a structured-clone round trip through IndexedDB. */
  takenAt: string | null;
  blob: Blob;
  /** Absent on pre-pilot durable items: those retain their original legacy delivery path. */
  protocol?: 'managed-photo-v1';
  sha256?: string;
  uploadId?: string;
  receipt?: MediaUploadReceipt;
}

/**
 * A refusal that came from the server, carrying the status — which is the whole difference
 * between "try again in a minute" and "this photo is never going to be accepted".
 * A `fetch` that never got an answer throws something else, and that is the signal for
 * "no connection", not "refused".
 */
export class UploadError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /**
     * The server said "not yet" rather than "no": a photo for a ticket whose flag has not
     * landed (ticket 03). The queue keeps it and asks again, whatever the status says.
     */
    readonly retryLater = false,
    readonly code?: MediaHoldCode,
    readonly retryAfterMs?: number,
    readonly pause = false,
  ) {
    super(message);
    this.name = 'UploadError';
  }
}

const HOLD_MESSAGES: Record<MediaHoldCode, string> = {
  PILOT_DISABLED: 'New photo uploads are paused.',
  PROOF_REQUIRED: 'Photo uploads are not ready yet.',
  INVENTORY_REQUIRED: 'Photo storage is being checked.',
  COMPANY_STORAGE_FULL: 'Your company’s photo storage is full.',
  PLATFORM_STORAGE_FULL: 'Photo uploads are temporarily paused.',
  BUDGET_HOLD: 'Photo uploads are paused for now.',
  RATE_LIMITED: 'Photo uploads are busy. This photo will try again shortly.',
  UPLOAD_BUSY: 'This photo is still uploading. Checking again shortly.',
  RECOVERY_REQUIRED: 'Checking whether this photo was saved. Try again later.',
  PROCESSING_FAILED: 'A viewing copy could not be made.',
  INVALID_PHOTO: 'This file could not be accepted as a photo.',
  INVALID_RECEIPT: 'The server did not confirm this photo.',
};

export function photoHoldMessage(
  code: string | null | undefined,
  localOriginal = true,
): string {
  const message =
    code && code in HOLD_MESSAGES
      ? HOLD_MESSAGES[code as MediaHoldCode]
      : 'Photo uploads are paused. Review photo storage.';
  return localOriginal ? `${message} The original is still on this phone.` : message;
}

/** Server receipts, not HTTP success alone, authorize removing bytes from this device. */
export async function uploadManagedImage(
  payload: UploadPayload,
  controls: {
    checkpoint(receipt: MediaUploadReceipt): Promise<void>;
    beforeRequest(): void;
    /** One explicit Retry action. Connectivity and background retries never set this. */
    retryUnknown?: boolean;
  },
): Promise<{ id: string }> {
  let expectedUploadId = payload.uploadId;
  const request = async (url: string, init: RequestInit): Promise<MediaUploadReceipt> => {
    controls.beforeRequest();
    const response = await fetch(url, {
      ...init,
      credentials: 'include',
      cache: 'no-store',
    });
    const body: unknown = await response.json().catch(() => null);
    if (!response.ok) {
      const error = body as {
        code?: MediaHoldCode;
        error?: string;
        retryAfterSeconds?: number;
      } | null;
      const header = response.headers.get('Retry-After');
      const seconds =
        header !== null && /^\d+$/.test(header)
          ? Number(header)
          : error?.retryAfterSeconds;
      const retryAfterMs =
        seconds && seconds > 0
          ? seconds * 1000
          : header
            ? Math.max(0, Date.parse(header) - Date.now()) || undefined
            : undefined;
      throw new UploadError(
        error?.code
          ? photoHoldMessage(error.code)
          : (error?.error ??
            'The photo could not be sent. Its original stays on this phone.'),
        response.status,
        false,
        error?.code,
        retryAfterMs,
        response.status === 409 && error?.code !== 'UPLOAD_BUSY',
      );
    }
    const parsed = mediaUploadReceipt.safeParse(body);
    if (!parsed.success || (parsed.data.durableOriginal && !parsed.data.attachmentId)) {
      throw new UploadError(
        'The server didn’t confirm that photo was stored',
        response.status,
        false,
        'INVALID_RECEIPT',
        undefined,
        true,
      );
    }
    const result = parsed.data;
    if (expectedUploadId && result.uploadId !== expectedUploadId) {
      throw new UploadError(
        'The server didn’t confirm this photo’s upload',
        response.status,
        false,
        'INVALID_RECEIPT',
        undefined,
        true,
      );
    }
    expectedUploadId = result.uploadId;
    await controls.checkpoint(result);
    return result;
  };
  let result = payload.uploadId
    ? await request(`/api/media/photos/${payload.uploadId}/status`, { method: 'GET' })
    : await request('/api/media/photos/initiate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientId: payload.clientId,
          mutationId: payload.mutationId,
          entityType: payload.entityType,
          entityId: payload.entityId,
          filename: payload.filename,
          contentType: payload.contentType,
          byteSize: payload.blob.size,
          sha256: payload.sha256,
          takenAt: payload.takenAt,
        }),
      });
  if (result.durableOriginal && result.attachmentId) return { id: result.attachmentId };
  if (
    controls.retryUnknown &&
    (result.state === 'unknown' ||
      result.state === 'held' ||
      result.holdCode === 'RECOVERY_REQUIRED')
  ) {
    // The server either recovers the existing original or confirms a safe new attempt.
    // Its receipt is checkpointed before sending any bytes, just like initiation.
    result = await request(`/api/media/photos/${result.uploadId}/retry`, {
      method: 'POST',
    });
    if (result.durableOriginal && result.attachmentId) return { id: result.attachmentId };
  }
  if (result.state === 'reserved' && !result.holdCode) {
    result = await request(`/api/media/photos/${result.uploadId}/content`, {
      method: 'PUT',
      headers: { 'Content-Type': payload.contentType },
      body: payload.blob,
    });
    if (result.durableOriginal && result.attachmentId) return { id: result.attachmentId };
  }
  const code =
    result.holdCode ??
    (result.state === 'uploading' ? 'UPLOAD_BUSY' : 'RECOVERY_REQUIRED');
  const temporary = code === 'UPLOAD_BUSY' || code === 'RATE_LIMITED';
  throw new UploadError(
    photoHoldMessage(code),
    temporary ? 429 : 409,
    false,
    code,
    (result.retryAfterSeconds ?? (temporary ? 30 : 0)) * 1000 || undefined,
    !temporary,
  );
}

/**
 * Send one prepared image. Raw bytes with the metadata in the query string — the server
 * has no multipart parser, on purpose.
 */
export async function uploadImage(payload: UploadPayload): Promise<{ id: string }> {
  const params = new URLSearchParams({
    entityType: payload.entityType,
    entityId: payload.entityId,
    kind: 'photo',
    filename: payload.filename,
    clientId: payload.clientId,
    mutationId: payload.mutationId,
  });
  if (payload.width) params.set('width', String(payload.width));
  if (payload.height) params.set('height', String(payload.height));
  if (payload.takenAt) params.set('takenAt', payload.takenAt);

  const res = await fetch(`/api/uploads?${params.toString()}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': payload.contentType },
    body: payload.blob,
  });
  if (!res.ok) {
    // The server writes these for a person holding a phone; show what it said.
    const body = (await res.json().catch(() => null)) as {
      error?: string;
      retryLater?: unknown;
    } | null;
    throw new UploadError(
      body?.error ?? `Upload failed (${res.status})`,
      res.status,
      body?.retryLater === true,
    );
  }
  // The queue deletes its only copy on the strength of this answer, so it has to be an
  // answer: `res.ok` spans 200–299, and a cache or a proxy can produce one with no body at
  // all. No id, no confirmation that anything was stored.
  const stored = (await res.json().catch(() => null)) as { id?: unknown } | null;
  if (typeof stored?.id !== 'string') {
    throw new UploadError('The server didn’t confirm that photo was stored', res.status);
  }
  return { id: stored.id };
}
